import React, { memo, useCallback } from "react"
import { Button, List, Tag } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTrash,
  faPlus,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons"

// Define the query item structure
interface QueryItem {
  /** Unique identifier for the item */
  key: string
  /** Display title for the query */
  title: string
  /** Flag indicating if this is the "Create New Query" item */
  isCreateNew: boolean
}

// Define the props interface for the QueryListItem component
interface QueryListItemProps {
  /** The query item to display */
  item: QueryItem
  /** Currently selected query name */
  selectedQuery: string
  /** Callback function for delete action */
  onDelete: (e: React.MouseEvent, title: string) => void
  /** Callback function for select action */
  onSelect: (e: Event | null, title: string) => void
  /** Callback function for create new action */
  onCreate: () => void
  /** A short tag shown after the title, e.g. "on load". */
  badge?: string
}

/**
 * Individual list item component for query display.
 * Memoized to prevent unnecessary rerenders.
 *
 * @component
 * @param props - Component props
 * @returns Rendered list item component
 */
const QueryListItem: React.FC<QueryListItemProps> = ({
  item,
  selectedQuery,
  onDelete,
  onSelect,
  onCreate,
  badge,
}) => {
  /**
   * Handles the delete action for a query item.
   * Prevents event propagation to avoid triggering select action.
   *
   * @param e - The click event
   */
  const handleDelete = useCallback(
    (e: React.MouseEvent): void => {
      e.stopPropagation()
      onDelete(e, item.title)
    },
    [item.title, onDelete]
  )

  /**
   * Handles click events on the list item.
   * Determines whether to trigger create or select action.
   */
  const handleClick = useCallback((): void => {
    if (item.isCreateNew) {
      onCreate()
    } else {
      onSelect(null, item.title)
    }
  }, [item.isCreateNew, item.title, onCreate, onSelect])

  return (
    <List.Item
      key={item.key}
      actions={
        !item.isCreateNew
          ? [
              // A row action: outlined and red, never the solid main one
              <Button
                key={item.key}
                danger
                aria-label="Delete query"
                title="Delete query"
                className="mr-5"
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={handleDelete}
              />,
            ]
          : undefined
      }
      className={`cursor-pointer ${
        item.isCreateNew
          ? "bg-[color-mix(in_srgb,var(--dq-brand)_5%,transparent)] hover:bg-[var(--dq-brand-soft)]"
          : selectedQuery === item.title
          ? "bg-[color-mix(in_srgb,var(--dq-brand)_20%,transparent)] hover:bg-[color-mix(in_srgb,var(--dq-brand)_30%,transparent)]"
          : "hover:bg-gray-100"
      }`}
      onClick={handleClick}
    >
      <List.Item.Meta
        className="p-3 px-5"
        title={
          item.isCreateNew ? (
            <div className="flex items-center font-semibold text-[var(--dq-brand)] mt-1">
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {item.title}
            </div>
          ) : (
            <div className="flex items-center">
              {selectedQuery === item.title && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="mr-2 text-[var(--dq-brand)]"
                />
              )}
              <span
                className={selectedQuery === item.title ? "text-[var(--dq-brand)]" : ""}
              >
                {item.title}
              </span>
              {badge && <Tag className="ml-2">{badge}</Tag>}
            </div>
          )
        }
      />
    </List.Item>
  )
}

export default memo(QueryListItem)
